import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from 'api/axiosInstance';
import apiUrls from 'api/apiUrls';

import Sidebar from 'components/sidebar/Sidebar';
import ComplexTable from 'views/admin/default/components/ComplexTable';
import GlobalModal from 'components/GlobalModal';
import InterfaceSelect from 'components/select';
import Default from 'components/fields/InputField';

import {
  Box,
  Flex,
  Heading,
  Button,
  Input,
  FormControl,
  FormLabel,
  ModalFooter,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@chakra-ui/react';
import Card from 'components/card/Card';

import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import ViewMoreModal from 'components/ViewMoreModal';
import GlobalAlertDialog from 'components/GlobalAlertDialog';
const Template = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const cancelRef = useRef();

  const [collapsed, setCollapsed] = useState(false);
  const [templates, setTemplates] = useState([]);

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const [rows, setRows] = useState([{ key: '', unit: '', datatype: 'float' }]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewModalKeys, setViewModalKeys] = useState([]);

  /* ---------------- DATATYPE OPTIONS ---------------- */

  const datatypeOptions = {
    Integer: 'integer',
    Float: 'float',
    Double: 'double',
    Long: 'long',
    String: 'string',
    Boolean: 'boolean',
    JSON: 'json',
    Timestamp: 'timestamp',
  };

  /* ---------------- FETCH TEMPLATES ---------------- */

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axiosInstance.get(apiUrls.getTemplates());
      const assets = res.data.data || [];

      const templatesWithSchema = await Promise.all(
        assets.map(async (asset) => {
          try {
            const attr = await axiosInstance.get(
              apiUrls.getTemplateSchema(asset.id.id),
            );

            const schema =
              attr.data.find((a) => a.key === 'schema')?.value || [];

            return {
              id: asset.id.id,
              name: asset.name,
              description: asset.additionalInfo?.description || '',
              keys: schema.map((k) => k.key),
              schema,
            };
          } catch {
            return {
              id: asset.id.id,
              name: asset.name,
              description: asset.additionalInfo?.description || '',
              keys: [],
              schema: [],
            };
          }
        }),
      );

      setTemplates(templatesWithSchema);
    } catch {
      toast({
        title: 'Failed to fetch templates',
        status: 'error',
      });
    }
  };

  /* ---------------- TEMPLATE TABLE ---------------- */

  const templateColumns = [
    {
      header: 'S.No',
      id: 'serialNumber',
      cell: (info) => info.row.index + 1,
    },
    {
      header: 'Name',
      accessorKey: 'name',
    },
    {
      header: 'Description',
      accessorKey: 'description',
    },
    {
      header: 'Keys',
      accessorKey: 'keys',
      cell: (info) => {
        const keys = info.row.original.keys || [];
        if (!keys.length) return '-';

        const visibleKeys = keys.slice(0, 2);
        const hiddenKeys = keys.slice(2);

        return (
          <Flex wrap="wrap" gap={1} align="center">
            {visibleKeys.map((key, idx) => (
              <Box key={idx} as="span">
                {key}
                {idx < visibleKeys.length - 1 && ','}
              </Box>
            ))}
            {hiddenKeys.length > 0 && (
              <Button
                size="xs"
                colorScheme="blue"
                ml={2}
                onClick={() => {
                  setViewModalKeys(keys);
                  setViewModalOpen(true);
                }}
              >
                More
              </Button>
            )}
          </Flex>
        );
      },
    },
    {
      header: 'Actions',
      id: 'actions',
      cell: (info) => (
        <Flex gap={2}>
          <IconButton
            icon={<EditIcon />}
            size="sm"
            colorScheme="blue"
            onClick={() => handleEditTemplate(info.row.original)}
          />

          <IconButton
            icon={<DeleteIcon />}
            size="sm"
            colorScheme="red"
            onClick={() => handleDeleteClick(info.row.original)}
          />
        </Flex>
      ),
    },
  ];

  /* ---------------- SCHEMA TABLE ---------------- */

  const schemaColumns = [
    {
      header: 'Key',
      accessorKey: 'key',
      cell: (info) => {
        const index = info.row.index;

        return (
          <Default
            size="sm"
            placeholder="temperature"
            value={rows[index]?.key || ''}
            onChange={(e) => handleRowChange(index, 'key', e.target.value)}
          />
        );
      },
    },
    {
      header: 'Unit',
      accessorKey: 'unit',
      cell: (info) => {
        const index = info.row.index;

        return (
          <Default
            size="sm"
            placeholder="°C"
            value={rows[index]?.unit || ''}
            onChange={(e) => handleRowChange(index, 'unit', e.target.value)}
          />
        );
      },
    },
    {
      header: 'Datatype',
      accessorKey: 'datatype',
      cell: ({ row }) => {
        const index = row.index;

        return (
          <InterfaceSelect
            value={rows[index]?.datatype}
            options={datatypeOptions}
            onChange={(selected) => {
              const updated = [...rows];
              updated[index].datatype = selected;
              setRows(updated);
            }}
          />
        );
      },
    },
    {
      header: 'Delete',
      id: 'delete',
      cell: (info) => {
        const index = info.row.index;

        return (
          <Flex justify="center">
            <IconButton
              icon={<DeleteIcon />}
              size="xs"
              colorScheme="red"
              onClick={() => removeRow(index)}
            />
          </Flex>
        );
      },
    },
  ];

  /* ---------------- ROW HANDLERS ---------------- */

  const addRow = () => {
    setRows((prev) => [...prev, { key: '', unit: '', datatype: 'float' }]);
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRowChange = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  /* ---------------- VALIDATION ---------------- */

  const validateTemplate = () => {
    if (!formData.name) {
      toast({ title: 'Template name required', status: 'error' });
      return false;
    }

    const keys = rows.map((r) => r.key);

    if (keys.some((k) => !k)) {
      toast({ title: 'Keys cannot be empty', status: 'error' });
      return false;
    }

    const duplicates = keys.filter((k, i) => keys.indexOf(k) !== i);

    if (duplicates.length) {
      toast({ title: 'Duplicate keys not allowed', status: 'error' });
      return false;
    }

    return true;
  };

  /* ---------------- ADD TEMPLATE ---------------- */

  const handleAddTemplate = () => {
    setIsEditMode(false);
    setSelectedTemplateId(null);

    setFormData({ name: '', description: '' });
    setRows([{ key: '', unit: '', datatype: 'float' }]);

    onOpen();
  };

  /* ---------------- EDIT TEMPLATE ---------------- */

  const handleEditTemplate = (template) => {
    setIsEditMode(true);
    setSelectedTemplateId(template.id);

    setFormData({
      name: template.name,
      description: template.description,
    });

    setRows(template.schema || []);

    onOpen();
  };

  /* ---------------- DELETE TEMPLATE ---------------- */

  const handleDeleteClick = (template) => {
    setDeleteTemplateId(template.id);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axiosInstance.delete(apiUrls.getTemplateInfo(deleteTemplateId));

      toast({
        title: 'Template deleted',
        status: 'success',
      });

      fetchTemplates();
    } catch {
      toast({
        title: 'Failed to delete template',
        status: 'error',
      });
    }

    setIsDeleteOpen(false);
  };

  /* ---------------- SAVE TEMPLATE ---------------- */

  const handleSave = async () => {
    if (!validateTemplate()) return;

    try {
      let assetId = selectedTemplateId;

      if (!isEditMode) {
        const assetRes = await axiosInstance.post(apiUrls.saveTemplateAsset, {
          name: formData.name,
          type: 'site-template',
          label: 'Template',
          additionalInfo: { description: formData.description },
        });

        assetId = assetRes.data.id.id;
      }

      await axiosInstance.post(apiUrls.saveTemplateSchema(assetId), {
        schema: rows,
      });

      toast({
        title: isEditMode ? 'Template updated' : 'Template created',
        status: 'success',
      });

      onClose();
      fetchTemplates();
    } catch {
      toast({
        title: 'Failed to save template',
        status: 'error',
      });
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <Flex>
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <Box ml={collapsed ? '60px' : '240px'} p={6} w="100%">
        <Flex justify="space-between" mb={6}>
          <Heading size="lg">Templates</Heading>

          <Button
            variant="outline"
            size="sm"
            border="2px"
            colorScheme="green"
            onClick={handleAddTemplate}
          >
            Add Template
          </Button>
        </Flex>

        <ComplexTable
          title="Templates"
          columnsData={templateColumns}
          tableData={templates}
          enableLocalPagination={true}
          pageSize={10}
        />

        {/* Modal */}

        <GlobalModal
          isOpen={isOpen}
          onClose={onClose}
          closeOnOverlayClick={false}
          size="6xl"
          title={isEditMode ? 'Edit Template' : 'Add Template'}
          footer={
            <ModalFooter>
              <Button
                colorScheme="green"
                size="sm"
                variant="outline"
                mr={3}
                onClick={handleSave}
              >
                Save
              </Button>

              <Button variant="outline" size="sm" onClick={onClose}>
                Cancel
              </Button>
            </ModalFooter>
          }
        >
          {/* <Card> */}
          <Flex gap={6} mb={6}>
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Default
                size="sm"
                placeholder="Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Default
                size="sm"
                placeholder="Description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    description: e.target.value,
                  })
                }
              />
            </FormControl>
          </Flex>

          {/* </Card> */}

          <Flex justify="end" mb={4} mt="4">
            <Button
              size="sm"
              // variant="outline"
              colorScheme="teal"
              borderWidth="2px"
              // leftIcon={<AddIcon />}
              onClick={addRow}
            >
              Add Key
            </Button>
          </Flex>

          <Box maxH="400px" overflowY="auto">
            <ComplexTable
              title="Keys"
              columnsData={schemaColumns}
              tableData={rows}
              enableLocalPagination={false}
            />
          </Box>
        </GlobalModal>

        <GlobalAlertDialog
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={confirmDelete}
          title="Delete Template"
          body="Are you sure you want to delete this template? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          showCancel={true}
        />
        <ViewMoreModal
          isOpen={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          items={viewModalKeys}
          title="Keys"
          pageSize={16}
          width="800px"
        />
      </Box>
    </Flex>
  );
};

export default Template;
